import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import { makeStyles } from "helpers/StyleHelper";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import {
  ITemplateAdmin,
  ResponseCode,
  ResponseOfTemplateAdmin,
  TemplateSaveRequest,
  TemplateVersionSaveRequest,
} from "services/KnowfullyAdminClient";
import {
  CommonFormProps,
  Severity,
  SnackbarMessageProps,
} from "types/common-types";
import translateError from "utils/translateError";
import ContentTemplateDesignForm from "./ContentTemplateDesignForm";
import ContentTemplateDetailsForm from "./ContentTemplateDetailsForm";

const useStyles = makeStyles((theme: Theme) => ({
  expandedPanel: {
    backgroundColor: theme.palette.secondary.light,
  },
}));

export type ContentTemplateFormProps = {
  templateId?: string | undefined;
} & CommonFormProps<ITemplateAdmin, TemplateSaveRequest>;

const ContentTemplateForm = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { registerPromise } = useTrashablePromises();
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const {
    templateService,
    contentTemplatesUrl,
    contentTemplateEditUrl,
  } = useGlobalContext();

  const { id } = useParams<{
    id: string;
  }>();

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const [locked, lockedActions] = useBoolean(id === undefined);

  const [expandedSection, setExpandedSection] = useState<string | null>(
    "details"
  );

  const templateResponse = useQuery<ResponseOfTemplateAdmin>(
    ["contentTemplate", id],
    () => templateService.getTemplate(id!),
    {
      enabled: !!id,
    }
  );

  const template = templateResponse.data?.data;
  const title = template?.id
    ? template.name || t({ id: "templateFormEditTitle" })
    : t({ id: "templateFormCreateTitle" });
  useTitle(title);

  if (templateResponse.isLoading) return <Loader />;
  if (templateResponse.isError) {
    console.log(templateResponse.error);

    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="templateLoadError" />
        </Alert>
      </Box>
    );
  }

  const formProps = {
    onDirty: lockedActions.setTrue,
    onClean: lockedActions.setFalse,
    lockedActions,
  };

  const accordionProps = (section: string) => {
    return {
      square: true,
      expanded: expandedSection === section,
      onChange: toggleExpansion(section),
      disabled: locked && expandedSection !== section,
    };
  };

  const toggleExpansion = (section: string) => (e: any, expanded: boolean) => {
    if (locked || !id) return;
    setExpandedSection(expanded ? section : null);
  };

  const cleanCache = (id: string) => {
    queryClient.invalidateQueries("placeholders");
    queryClient.invalidateQueries("contentTemplates");
    queryClient.invalidateQueries(["contentTemplate", id]);
    queryClient.invalidateQueries(["templatePublished", id]);
  };

  const onSubmit = async (values: TemplateSaveRequest) => {
    try {
      const response = await registerPromise(
        templateService.saveTemplate(values)
      );

      cleanCache(response.id);

      lockedActions.setFalse();

      if (!template?.id) {
        navigate(contentTemplateEditUrl(response.id));
      }

      return response;
    } catch (err: any) {
      console.log(err);
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  const onSubmitVersion = async (values: TemplateVersionSaveRequest) => {
    try {
      const response = await registerPromise(
        templateService.saveTemplateVersion(values)
      );

      if (response?.responseCode === ResponseCode.OK) {
        template!.templateVersionId = response.id;

        queryClient.invalidateQueries("contentTemplates");
        queryClient.invalidateQueries(["contentTemplate", template!.id]);
        lockedActions.setFalse();
      }

      return response;
    } catch (err: any) {
      console.log(err);
      if (
        err.status === 200 &&
        err.data?.responseCode === ResponseCode.BusinessRuleFailed
      ) {
        setSnackbarMessage(
          new SnackbarMessageProps(translateError(err, t), Severity.Error)
        );
      }

      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  const onPublishVersion = async () => {
    try {
      const response = await registerPromise(
        templateService.publishTemplateVersion(
          template!.id!,
          template!.templateVersionId!
        )
      );

      if (response?.responseCode === ResponseCode.OK) {
        cleanCache(template!.id!);
      }
    } catch (err: any) {
      console.log(err);
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  const onDiscardDraft = async () => {
    try {
      await registerPromise(
        templateService.discardTemplateVersion(template!.templateVersionId!)
      );

      window.location.reload();
    } catch (err: any) {
      console.log(err);
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  return (
    <>
      <RedirectBackButton
        redirectRoute={contentTemplatesUrl()}
        labelKey="backToListButtonLabel"
        style={{ marginRight: "10px" }}
      />

      <Box my={2}>
        <Typography variant="h5">
          {template?.id ? (
            <T id="templateFormEditTitle" />
          ) : (
            <T id="templateFormCreateTitle" />
          )}
        </Typography>
      </Box>

      <Accordion {...accordionProps("details")}>
        <AccordionSummary
          style={{ margin: 0 }}
          expandIcon={<ExpandMoreIcon />}
          sx={accordionProps("details").expanded ? styles.expandedPanel : null}
          data-cy="accordionSummaryButton"
        >
          <Typography>
            <T id="detailsLabel" />
          </Typography>
        </AccordionSummary>
        <AccordionDetails style={{ display: "inherit" }}>
          <ContentTemplateDetailsForm
            {...formProps}
            entity={template}
            onSubmit={onSubmit}
          />
        </AccordionDetails>
      </Accordion>
      <Tooltip
        title={
          id == null ? t({ id: "accordionDisabledToCreationMessage" }) : ""
        }
      >
        <Accordion {...accordionProps("design")}>
          <AccordionSummary
            style={{ margin: 0 }}
            expandIcon={<ExpandMoreIcon />}
            sx={accordionProps("design").expanded ? styles.expandedPanel : null}
          >
            <Typography>
              {template?.templateVersionStatusId == null ? (
                <T id="contentLabel" />
              ) : template?.isDraft ? (
                <T id="contentDraftLabel" />
              ) : (
                <T id="contentPublishedLabel" />
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails style={{ display: "inherit" }}>
            <ContentTemplateDesignForm
              {...formProps}
              entity={template}
              onSubmit={onSubmitVersion}
              onPublishVersion={onPublishVersion}
              onDiscardDraft={onDiscardDraft}
            />
          </AccordionDetails>
        </Accordion>
      </Tooltip>
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default ContentTemplateForm;
